import { Send, Brain } from "lucide-react";

const examples = [
  ["WORK", "A colleague has become distant after I received more responsibility. What might explain the change?"],
  ["RELATIONSHIP", "Someone close to me says everything is fine, but their behavior has changed. How should I interpret it?"],
  ["DECISION", "I keep postponing an important decision even though I know the options. What could be driving that?"],
];

function displayLabel(value, fallback = "Behavioral reading") {
  if (!value) return fallback;
  return String(value).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, (m) => m.toUpperCase());
}

function publicText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.replace(/\byou's\b/gi, "your").replace(/\bThe user\b/g, "You").replace(/\bthe user\b/g, "you");
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(publicText).filter(Boolean).join("; ");
  if (typeof value === "object") {
    return value.description || value.text || value.hypothesis || value.explanation || value.rationale || value.claim || JSON.stringify(value);
  }
  return String(value);
}

function displayTitle(value) {
  return String(value || "Untitled situation")
    .replace(/^(LIVE_INFERENCE_TEST(?:_[A-Z0-9]+)*|TEST_[A-Z0-9_]+|LATENCY_CHECK_PRIVATE_REASONING)_[a-f0-9]*:?\s*/i, "")
    .replace(/^(LIVE_INFERENCE_TEST(?:_[A-Z0-9]+)*|TEST_[A-Z0-9_]+|LATENCY_CHECK_PRIVATE_REASONING):?\s*/i, "")
    .trim() || "Untitled situation";
}

export function HBMSituationWorkspace({
  input,
  setInput,
  busy,
  situation,
  onAnalyze,
  onShare,
  followUp,
  setFollowUp,
  onContinue,
  onForceSynthesize,
  onKeepProbing,
  onCloseSituation,
}) {
  const investigation = situation?.analysis?.investigation || {};
  const turns = situation?.turns || [];
  return (
    <section className="hbm-social-main-workspace" data-testid="hbm-social-main-workspace">
      {!situation && (
        <div className="hbm-social-welcome" data-testid="hbm-social-private-workspace">
          <span>HUMANE BEHAVIOR MODEL</span>
          <h1>Private behavioral reasoning.</h1>
          <p>Describe the exact issue. HBM will identify the real question, separate evidence from interpretation, and give you the next useful answer.</p>
          <div className="hbm-social-example-grid">
            {examples.map(([label, text]) => (
              <button key={label} type="button" onClick={() => setInput(text)} data-testid={`hbm-social-example-${label.toLowerCase()}-button`}>
                <small>{label}</small>
                <b>{text}</b>
              </button>
            ))}
          </div>
        </div>
      )}

      <form className="hbm-social-input-card" onSubmit={onAnalyze} data-testid="hbm-social-situation-form">
        <textarea
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Describe the situation…"
          disabled={busy}
          maxLength={12000}
          data-testid="hbm-social-situation-input-textarea"
        />
        <footer>
          <span>Enter a real situation · private by default</span>
          <div><small data-testid="hbm-social-input-character-count">{input.length.toLocaleString()} / 12,000</small><button type="button" onClick={(event) => event.currentTarget.form?.requestSubmit()} disabled={busy || input.trim().length < 8} data-testid="hbm-social-analyze-button"><Send size={14} /> Run HBM</button></div>
        </footer>
      </form>

      {situation && (
        <section className="hbm-social-card hbm-social-answer-card" data-testid="hbm-social-active-situation">
          <header>
            <span><Brain size={15} /> HBM REASONING</span>
            <b>{displayLabel(investigation.status || situation.analysis?.domain || "Behavioral read")}</b>
          </header>
          {situation.analysis?.service_message && (
            <div className="hbm-social-service-message" data-testid="hbm-social-service-message">
              {publicText(situation.analysis.service_message)}
            </div>
          )}
          {turns.length > 0 && (
            <div className="hbm-situation-thread" data-testid="private-workspace-thread">
              {turns.slice(-8).map((turn) => (
                <article key={turn.id} className={turn.role === "user" ? "user" : "assistant"} data-testid={`private-workspace-thread-${turn.role}-turn`}>
                  <span>{turn.role === "user" ? "You" : "HBM"}</span>
                  <p>{turn.role === "user" ? publicText(turn.user_text_or_answer_ref) : publicText(turn.payload_json?.service_message || turn.payload_json?.investigation?.primary_question || turn.payload_json?.synthesis_preview || turn.payload_json?.next_best_test || "Updated the situation.")}</p>
                </article>
              ))}
            </div>
          )}
          {investigation.primary_question && (
            <section className="hbm-primary-question" data-testid="private-workspace-primary-question">
              <span>Core issue</span>
              <p data-testid="private-reasoning-initial-identification">{publicText(situation.analysis?.provider_reasoning?.initial_identification || investigation.primary_question)}</p>
            </section>
          )}
          {!investigation.primary_question && situation.analysis?.provider_reasoning?.initial_identification && (
            <section className="hbm-primary-question" data-testid="private-workspace-primary-question">
              <span>Core issue</span>
              <p>{publicText(situation.analysis.provider_reasoning.initial_identification)}</p>
            </section>
          )}
          <div className="hbm-social-answer-body" data-testid="hbm-social-analysis-card">
            <section data-testid="private-workspace-observation-panel">
              <h2>Direct observations</h2>
              {(situation.analysis?.direct_observations || []).slice(0, 3).map((item, index) => <p key={`obs-${index}`}>{publicText(item)}</p>)}
            </section>
            <section>
              <h2>Inferred motives & dynamics</h2>
              <ul>
                {(situation.analysis?.inferred_motives || situation.analysis?.hypotheses || []).slice(0, 3).map((item, index) => <li key={`${index}-${publicText(item).slice(0, 32)}`}>{publicText(item)}</li>)}
              </ul>
            </section>
            <section data-testid="hbm-social-alternatives-panel">
              <h2>Other plausible reads</h2>
              {(situation.analysis?.alternative_explanations || []).slice(0, 3).map((item, index) => <p key={`${index}-${publicText(item).slice(0, 32)}`}>{publicText(item)}</p>)}
            </section>
            <section data-testid="hbm-social-observation-panel">
              <h2>What to check</h2>
              {(situation.analysis?.missing_information || []).slice(0, 2).map((item, index) => <p key={`missing-${index}-${publicText(item).slice(0, 20)}`}><b>Missing:</b> {publicText(item)}</p>)}
              {(situation.analysis?.what_to_observe || []).slice(0, 2).map((item, index) => <p key={`observe-${index}-${publicText(item).slice(0, 20)}`}><b>Observe:</b> {publicText(item)}</p>)}
              {publicText(situation.analysis?.next_best_test) && <p><b>Next:</b> {publicText(situation.analysis?.next_best_test)}</p>}
            </section>
            <section className="hbm-social-outcome-inline" data-testid="hbm-social-outcome-panel">
              <h2>Continue this situation</h2>
              <textarea value={followUp} onChange={(e) => setFollowUp(e.target.value)} placeholder="Answer the question, add new evidence, or correct a fact…" data-testid="private-workspace-follow-up-input" />
              <div className="hbm-social-result-actions">
                <button type="button" onClick={() => onContinue({})} disabled={busy || !followUp.trim()} data-testid="private-workspace-add-evidence-btn">Add new evidence</button>
                <button type="button" onClick={onForceSynthesize} disabled={busy} data-testid="private-workspace-force-synthesis-btn">Give me the best answer now</button>
                <button type="button" onClick={onKeepProbing} disabled={busy} data-testid="private-workspace-keep-probing-btn">Keep probing</button>
                <button type="button" onClick={onShare} disabled={busy} data-testid="private-workspace-share-as-situation-btn">Share as Situation</button>
                <button type="button" onClick={onCloseSituation} disabled={busy} data-testid="private-workspace-close-situation-btn">Close situation</button>
              </div>
            </section>
          </div>
        </section>
      )}
    </section>
  );
}